// Code generated by balancegen from the data layer (data/*/*.toml). DO NOT EDIT.
// Regenerate with scripts/gen-data.sh.
//
// The inputs of world generation, for the terrain lab ALONE
// (terrain-lab.md §5). Only client/src/terrainlab/ imports this module.
// The game client needs none of these figures, because the server
// sends the finished map, and gen/balance.ts must never grow a game
// table. Source: data/balance/world.toml and data/identity/names.toml.

import type { SizeRow } from '../terrainlab/engine/types';

// world.toml [world]
export const TRENCH_SPEED_FACTOR = 0.5;
export const TRENCH_LEVEL = 0.34;
export const RIDGE_LEVEL = 0.62;
export const MIN_FEATURE_CELLS = 3;
export const ANCHOR_RADIUS_FRAC = 0.38;

/** world.toml [world.noise]: the value noise of the classic terrain. */
export const NOISE = {
  gridSize: 16,
  featureCells: 9,
  ampFirst: 0.65,
  ampDecay: 0.5,
  octaves: 2,
} as const;

/** world.toml [world.sites]: the placement of the strategic objects and
 * the deposits (numbers.md §9). */
export const SITES = {
  depositMin: 2000,
  depositMax: 4800,
  depositSpacing: 185,
  cpSpacing: 520,
  anySpacing: 90,
  placeTries: 2400,
  objEdgeMarginK: 0.12,
  objAnchorClearance: 1000,
  objWindowStartHour: 18,
  objWindowRollHours: 3,
} as const;

/** world.toml [world.revised]: the terrain, the seaways and the sites of
 * the revised generator (terrain-lab.md §8, R1530, R1536). The covers are
 * percent; the seaway costs are integers; the px figures are core-world
 * figures. */
export const REVISED = {
  latticeSize: 40,
  featurePx: 200,
  featureScaleExp: 0.5,
  asymmetry: 0.25,
  ridgeCover: 20,
  trenchCover: 30,
  ridgeMaxSpanFrac: 0.18,
  gapCells: 3,
  maxSpanPasses: 8,
  seawayStepCost: 10,
  seawayTrenchCost: 10,
  seawayClearCells: 4,
  seawayClearCost: 4,
  seawayNoiseCost: 8,
  seawayJitterCost: 12,
  seawayCentreJitter: 0.12,
  seawayKeepFrac: 0.33,
  seawayKeepJitter: 0.25,
  maxDetour: 1.6,
  detourPasses: 4,
  spireMinPx: 300,
  spireMaxPx: 600,
  spireSepPx: 900,
  objectMaxFrac: 0.28,
  frontRowFrac: 0.1,
  rearRowFrac: 0.3,
  centreKeepoutFrac: 0.15,
  depositBeyondRearPx: 600,
  depositMiddleShare: 0.25,
  depositFrontWeight: 0.6,
  depositRearWeight: 1,
} as const;

/** world.toml [world.depth], [world.currents] and [world.fairways]: the
 * derived terrain of v2 (v2-the-deep.md §3.1, §5.1, §7.1, R2567). The
 * shares are fractions of the open water; the tide steps are factors of
 * the field. */
export const DEPTH = {
  shelfShare: 0.33,
  slopeShare: 0.34,
  rimWeight: 0.5,
} as const;

export const CURRENTS = {
  medianPxH: 100,
  medianUnits: 32,
  narrowGain: 2,
  tidePeriodSec: 21600,
  tideSteps: [1, 0.91, 0.65, 0.3, -0.05, -0.31, -0.4, -0.31, -0.05, 0.3, 0.65, 0.91] as readonly number[],
} as const;

export const FAIRWAYS = {
  centreNode: 0,
  keepOff: 3,
  keepOffWeight: 2,
  maxRoutePx: 3200,
  flowMin: 0.25,
  narrowCells: 5,
  nodeClearCells: 3,
  approachCells: 5,
  maxFairways: 8,
  widthPx: 60,
  buoySpacingPx: 160,
} as const;

/** world.toml [world.poi]: the points of interest and the dens of v2
 * (v2-the-deep.md §4.2, §8.2, R2607), from the stream roll seed + 13 (R2907). The
 * px figures are absolute; centreFrac is the radius of the centre disc as a
 * share of the world width and centreClearPx the site clearance in it (R2959); the rows follow SIZES. */
export const POI = {
  siteClearPx: 240,
  spacingPx: 400,
  placeTries: 200,
  gateMul: 2,
  gateReachCells: 3,
  centreFrac: 0.15,
  centreClearPx: 120,
} as const;

export const POI_SIZES: readonly { seats: number; vents: number; caches: number; lairs: number; gates: number; ruins: number; dens: number; kindLairs: number; brotherhoodDens: number; centreGates: number }[] = [
  { seats: 50, vents: 3, caches: 4, lairs: 1, gates: 3, ruins: 2, dens: 2, kindLairs: 2, brotherhoodDens: 2, centreGates: 1 },
  { seats: 100, vents: 5, caches: 6, lairs: 2, gates: 5, ruins: 3, dens: 3, kindLairs: 4, brotherhoodDens: 4, centreGates: 1 },
  { seats: 150, vents: 6, caches: 8, lairs: 2, gates: 6, ruins: 4, dens: 4, kindLairs: 6, brotherhoodDens: 6, centreGates: 2 },
  { seats: 200, vents: 8, caches: 10, lairs: 3, gates: 8, ruins: 5, dens: 5, kindLairs: 8, brotherhoodDens: 8, centreGates: 2 },
  { seats: 250, vents: 10, caches: 12, lairs: 4, gates: 10, ruins: 6, dens: 6, kindLairs: 10, brotherhoodDens: 10, centreGates: 2 },
];

/** npc.toml: the count of designs of each monster kind, in the order of
 * [[npc.monsters]], and of each brotherhood, in the order of
 * [[npc.brotherhoods]] (v2-the-deep.md §8.4, §8.5, R2719, R2720). The
 * generator draws a lair kind and a den brotherhood from them. */
export const NPC_KIND_DESIGNS: readonly number[] = [4, 4, 4, 4, 4];
export const NPC_BROTHERHOOD_DESIGNS: readonly number[] = [2, 2, 2, 2, 2];

/** npc.toml [[npc.brotherhoods]] cache_at: the place of the pirate cache
 * of each den design, by brotherhood and then in the order of `dens`, as
 * an offset in world px from the den cell centre (v2-the-deep.md §9.1,
 * R2859). The hub places the cache at the same point. */
export const NPC_DEN_CACHE_AT: readonly (readonly (readonly [number, number])[])[] = [
  [[3, -1], [-42, 34]],
  [[-14, 1], [24, 0]],
  [[24, 9], [6, 1]],
  [[17, 0], [12, 16]],
  [[0, -17], [3, 0]],
];

/** npc.toml: the class of each monster kind and its design names, in the
 * order of [[npc.monsters]], and the key of each brotherhood and its den
 * design names, in the order of [[npc.brotherhoods]] (v2-the-deep.md
 * §8.4, §8.5). The terrain lab names a lair and a den by them. */
export const NPC_KIND_DESIGN_NAMES: readonly (readonly string[])[] = [
  ['hinged_jaw', 'ring_maw', 'ram_plate', 'gullet_sack'],
  ['trapdoor', 'mantis_fold', 'ink_veil', 'lure_lamp'],
  ['quill_spine', 'brood_sac', 'siphon_tube', 'bud_gourd'],
  ['discharge_ray', 'twin_drum', 'coil_eel', 'lamp_crown'],
  ['dot_cloud', 'ribbon_school', 'ring_pack', 'comma_flock'],
];
export const NPC_KIND_CLASSES: readonly string[] = ['Gulper', 'Murkhide', 'Quillback', 'Thrummer', 'Myriad'];
export const NPC_DEN_DESIGN_NAMES: readonly (readonly string[])[] = [
  ['lantern_wreck', 'broken_ridge'],
  ['jaw_cut', 'rammed_pair'],
  ['sounding_ledge', 'survey_ship'],
  ['grappled_hauler', 'hook_spur'],
  ['down_funnel', 'sunken_dreadnought'],
];
export const NPC_BROTHERHOOD_KEYS: readonly string[] = ['lantern_kin', 'saltjaw', 'ninefathom', 'grapnel_company', 'undertow'];

/** npc.toml [[npc.sizes]]: the dens that sail a group, the first `groups`
 * dens of a world (hub npcReady); only such a den thinks and moves when it
 * is quiet. quiet_move_rings: the rings a quiet den walks out to (R2901).
 * deep.toml search.reach_px: the reach from a water cell centre to a cache
 * point (R2859). */
export const NPC_GROUPS: readonly { seats: number; groups: number }[] = [
  { seats: 50, groups: 2 },
  { seats: 100, groups: 4 },
  { seats: 150, groups: 6 },
  { seats: 200, groups: 8 },
  { seats: 250, groups: 10 },
];
export const NPC_QUIET_MOVE_RINGS = 8;
export const CACHE_REACH_PX = 47;

/** npc.toml: the hunting water of a pirate group and the range of a
 * monster (v2-the-deep.md §8.2, §8.3, §8.5, R2916). leash_px: a group takes
 * no target farther than this from its den (hub npcTarget). NPC_HUNT: the
 * `hunt` words of each brotherhood, in the order of [[npc.brotherhoods]].
 * NPC_KIND_AMBUSH_PX: ambush_px of each monster kind, 0 for a kind with
 * none. NPC_HOLD_APRON_RINGS: the hex rings of the apron of a hold or an
 * installation that no NPC hull enters (sim.HoldApronRings). */
export const NPC_LEASH_PX = 1600;
/** npc.toml sortie_every: the roam legs of a pirate group for each sortie
 * to the sortie water of its den (v2-the-deep.md §8.2, R2956). */
export const NPC_SORTIE_EVERY = 3;
export const NPC_HUNT: readonly (readonly string[])[] = [
  ['fairway', 'deep'],
  ['deep'],
  ['abyss'],
  ['lane', 'fairway'],
  ['abyss'],
];
export const NPC_KIND_AMBUSH_PX: readonly number[] = [0, 120, 0, 0, 0];
export const NPC_HOLD_APRON_RINGS = 3;

/** world.toml [[world.revised.sizes]], fewest seats first: the
 * installations per faction of the revised founding (terrain-lab.md §8.3,
 * R1536). The two rows are REVISED.frontRowFrac and REVISED.rearRowFrac on
 * every size (R1541). */
export const REVISED_SIZES: readonly { seats: number; installations: number }[] = [
  { seats: 50, installations: 1 },
  { seats: 100, installations: 2 },
  { seats: 150, installations: 2 },
  { seats: 200, installations: 3 },
  { seats: 250, installations: 3 },
];

/** world.toml [[world.sizes]], fewest seats first; the last row is the
 * core world (numbers.md §14.3). */
export const SIZES: readonly SizeRow[] = [
  { seats: 50, cols: 59, rows: 68, alloyFields: 15, biomassFields: 10, volatileFields: 5, holdSites: 1, poolSites: 1 },
  { seats: 100, cols: 84, rows: 97, alloyFields: 30, biomassFields: 20, volatileFields: 10, holdSites: 2, poolSites: 1 },
  { seats: 150, cols: 103, rows: 119, alloyFields: 45, biomassFields: 30, volatileFields: 15, holdSites: 2, poolSites: 2 },
  { seats: 200, cols: 119, rows: 137, alloyFields: 60, biomassFields: 40, volatileFields: 20, holdSites: 3, poolSites: 2 },
  { seats: 250, cols: 133, rows: 153, alloyFields: 75, biomassFields: 50, volatileFields: 25, holdSites: 3, poolSites: 2 },
];

/** The resource id of each deposit flavour row (names.toml
 * [[strings.names.deposits]]), in file order: world.depositRows. */
export const DEPOSIT_ROW_RESOURCES: readonly number[] = [1, 1, 2, 2, 3, 3];

/** The place names of the strategic objects (names.toml cp_names):
 * world.cpNames. */
export const CP_NAMES: readonly string[] = ['Cinder Shelf', 'Long Drift', 'Widow\'s Reach', 'Palegrave', 'Coldmouth', 'The Sill', 'Ashen Narrows', 'Barrowdeep', 'Fathom Bend', 'Stillcurrent', 'Gallows Trench', 'Quiet Marches'];

/** The kind name of each strategic object kind, keyed by ObjKind:
 * world.ObjKindName. */
export const OBJ_KIND_NAMES: Readonly<Record<number, string>> = {
  1: "Trench Throne",
  2: "Relay Spire",
  3: "Forge Vent",
  4: "Kelp Terrace",
  5: "Deep Battery",
  6: "Drydock Shoal",
  7: "Archive Vault",
  8: "Marshal Court",
  9: "Coral Ward",
  10: "Reef Breakwater",
};

/** The faction installation names (names.toml base_names):
 * world.baseNames, which world.FactionBaseNames shuffles. */
export const BASE_NAMES: readonly string[] = ['Haven', 'Bastion', 'Refuge', 'Trench Gate', 'Deep Hold', 'Cradle', 'Vault', 'Anchorage'];

/** The founding placement: hub.toml [hub.placement], world.toml
 * sites.base_spacing and faction_base_sonar, and world.BasesPerFaction. */
export const PLACEMENT = {
  basesPerFaction: 3,
  baseSpacing: 320,
  factionBaseSonar: 400,
  squadSize: 10,
  pocketSpacing: 800,
  baseSpread: 260,
  holdSpacing: 124,
  claimRadius: 115,
  forwardBaseFromCentre: 1000,
  rearBaseMinBack: 600,
  rearBaseMaxBack: 800,
  rearBaseSpread: 0.38,
  pocketShrink: 0.8,
  pocketFloorPx: 80,
  spreadGrow: 1.4,
  claimShrink: 0.8,
  middleBandLo: 0.33,
  middleBandSpan: 0.34,
  forwardDistBase: 150,
  forwardDistSpan: 450,
  forwardDistPerNth: 90,
} as const;

/** The revised founding: hub.toml [hub.placement.revised] (terrain-lab.md
 * §8.3, R1531, R1536). The rows and the centre keepout are in REVISED, and
 * the installations in REVISED_SIZES. A _frac figure is a share of the
 * width; install_step_px scales with the world size; the other px figures
 * are absolute. */
export const PLACEMENT_REVISED = {
  frontShare: 0,
  rowArcDeg: 50,
  rowJitterPx: 60,
  rowPocketSpacing: 400,
  middleSquads: 0,
  middleFrac: 0.07,
  middleSidePx: 340,
  holdSpread: 360,
  holdSpreadGrow: 1.2,
  spireGapPx: 100,
  spireSonar: 400,
  installStepPx: 20,
  installMaxSteps: 12,
} as const;
